import React, { useEffect, useState } from "react";
import { useMercuryContext, useMercuryDispatch } from "context";
import {
  Route,
  Routes,
  useLocation,
  RouteObject,
} from "react-router-dom";
import { Layout } from "antd";

import Message from "shared/components/Message";

import { loadCustomerRoutes } from "utils/loadCustomerRoutes";
import { globalRoutes } from "../../apps/globalRoutes";
import { Loader } from "shared/components/Components";

function Content() {
  const { Content } = Layout;
  const location = useLocation();

  const { userDetails, selectedApp } = useMercuryContext();
  const dispatch = useMercuryDispatch();

  const appsRaw: any[] = userDetails?.customerApplications ?? [];
  const [appRoutes, setAppRoutes] = useState<RouteObject[] | null>(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);


  const normalizedApps = appsRaw.map((a) =>
    typeof a === "string" ? { _id: a } : a || {}
  );


  // Default app selection
  useEffect(() => {
    if (selectedApp) return;
    if (!normalizedApps.length) return;
  
    const storedCA = localStorage.getItem("selectedCA");
    if (storedCA && normalizedApps.some((a: any) => a._id === storedCA)) {
      dispatch({ type: "SET_SELECTED_APP", payload: storedCA });
      return;
    }
  
    const defaultApp = normalizedApps.find((a: any) => a.active) || normalizedApps[0];
    dispatch({ type: "SET_SELECTED_APP", payload: defaultApp });
  }, [normalizedApps, selectedApp, dispatch]);

  const resolveAppName = (appId: any): string | null => {
    if (!appId) return null;

    const found: any = userDetails.customerApplicationDetails.find((c: any) => c._id === appId);
    return found ? found.name : null;
  };

  useEffect(() => {
    let mounted = true;

    async function loadRoutesForApp() {
      setAppRoutes(null);


      if (!selectedApp || !userDetails.customerApplicationDetails.length) {
        setLoadingRoutes(false);
        return;
      }
 
      const appName = resolveAppName(selectedApp);

      if (!appName) {
        setLoadingRoutes(false);
        return;
      }

      try {
        setLoadingRoutes(true);
        const routes = await loadCustomerRoutes(appName);

        if (!mounted) return;
        setAppRoutes(Array.isArray(routes) ? routes : []);
      } catch (err) {
        console.error("Error loading customer routes for", selectedApp, err);
        if (mounted) setAppRoutes([]);
      } finally {
        if (mounted) setLoadingRoutes(false);
      }
    }

    loadRoutesForApp();
    return () => {
      mounted = false;
    };
  }, [selectedApp, userDetails.customerApplicationDetails]);

  if (!normalizedApps.length) {
    return (
      <Content className="content-wrap">
        <div className="site-layout-content">
          <Message
            first="Sorry"
            second="It appears you don't have access to any applications in Mercury, please contact support."
            loading={false}
          />
        </div>
      </Content>
    );
  }

  return (
    <Content className="content-wrap">
      <div className="site-layout-content">
        {loadingRoutes || appRoutes === null ? (
          <Loader animate={true} logo={true} title={"Loading Customer Applications..."}/>
        ) : (
          <Routes location={location}>
            {globalRoutes.map((r, i) => (
              <Route
                key={`global-${i}`}
                path={r.path as string}
                element={r.element}
              />
            ))}

            {/* customer app routes */}
            {appRoutes.length > 0 ? (
              appRoutes.map((r, i) => (
                <Route
                  key={`app-${i}`}
                  path={r.path as string}
                  element={r.element}
                />
              ))
            ) : (
              <Route
                path="*"
                element={
                  <Message
                    first="No application routes"
                    second="This application does not expose any routes yet."
                    loading={false}
                  />
                }
              />
            )}
          </Routes>
        )}
      </div>
    </Content>
  );
}

export default Content;
